import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Background } from "./Background";
import { GeometricShapes } from "./GeometricShapes";
import { Modal } from "./Modal";
import { EarlyAccessForm } from "./EarlyAccessForm";
import { Logo } from "./Logo";
import { KnowledgeGraphAnimation } from "./KnowledgeGraphAnimation";
import { SpiralLearningAnimation } from "./SpiralLearningAnimation";
import { DocumentSecurityAnimation } from './DocumentSecurityAnimation';
import { BackgroundFloatingIcons } from './BackgroundFloatingIcons';
import { DocumentWithCredits } from './DocumentWithCredits';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100 },
  },
};

function Button() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <motion.div
        className="bg-[#0000bd] h-12 relative rounded-[4px] shrink-0 cursor-pointer shadow-md transition-colors"
        data-name="Button"
        whileHover={{ 
          scale: 1.05, 
          backgroundColor: "#4040FF"
        }}
        whileTap={{ scale: 0.95 }}
        onClick={handleOpenModal}
      >
        <div className="flex flex-row items-center justify-center relative size-full">
          <div className="box-border content-stretch flex flex-row gap-2 h-12 items-center justify-center px-4 py-2 relative">
            <div className="css-jjl3q5 flex flex-col font-[Work_Sans] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[18px] text-left text-neutral-50 text-nowrap">
              <p className="block leading-[28px] whitespace-pre">
                Request a Demo
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <EarlyAccessForm onClose={handleCloseModal} />
      </Modal>
    </>
  );
}

function Copy() {
  return (
    <motion.div 
      className="relative shrink-0 w-full max-w-[586px] z-10 px-4 sm:px-0" 
      data-name="Copy"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="box-border content-stretch flex flex-col gap-[21px] items-start justify-start p-0 relative w-full">
        <motion.h1 
          className="adjustLetterSpacing css-67t015 font-[Work_Sans] font-semibold leading-[1.2] text-[#001d1a] text-[24px] sm:text-[28px] md:text-[32px] tracking-[-0.225px] mb-6"
          variants={itemVariants}
        >
          <span className="block mb-0">Turn Your Team Knowledge Instantly</span>
          <span className="block">into Impactful Documents.</span>
        </motion.h1>
        <motion.div
          className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] w-full not-italic relative shrink-0 text-[#000000] text-[16px] text-left"
          variants={itemVariants}
        >
                      <p className="block mb-0">Sharpcut synthesises your team's scattered knowledge into high-quality first drafts, freeing your experts for valuable client work.</p>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Button />
        </motion.div>
      </div>
    </motion.div>
  );
}

function Container() {
  return (
    <div className="relative w-full" data-name="Container">
      <div className="box-border content-stretch flex flex-col gap-[30px] items-center justify-center p-0 relative w-full max-w-none lg:max-w-6xl lg:mx-auto md:flex-row md:gap-[80px] md:items-center">
        <Copy />
        {/* Document with Rolling Credits */}
        <div className="relative flex items-center justify-center shrink-0">
          <DocumentWithCredits />
        </div>
      </div>
    </div>
  );
}

function ValuePropositionSection() {
  return (
    <section className="w-full py-24 px-4 sm:px-6 md:px-9 bg-[#E8F0FF] relative z-20">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="adjustLetterSpacing css-67t015 font-[Work_Sans] font-semibold leading-[1.2] text-[#001d1a] text-[24px] sm:text-[28px] md:text-[32px] tracking-[-0.225px] mb-6">
            Stop Wasting Billable Hours.
          </h2>
          <p className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] text-[#000000] text-[16px] max-w-4xl mx-auto">
            Free your experts from chasing and stitching scattered knowledge for proposals. Sharpcut connects to your live knowledge to synthesise a first draft in minutes, cutting the total manual effort by over 80%.
          </p>
        </div>

        {/* Three Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Column 1 */}
          <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#0000bd] rounded-full flex items-center justify-center">
              <span className="material-icons-sharp text-white text-[32px]">schedule</span>
            </div>
            <h3 className="css-67t015 font-[Work_Sans] font-semibold text-[#001d1a] text-[20px] mb-3">
              Minutes, Not Days
            </h3>
            <p className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] text-[#000000] text-[16px]">
              Generate comprehensive first drafts in minutes instead of spending days collecting information
            </p>
          </div>

          {/* Column 2 */}
          <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#0000bd] rounded-full flex items-center justify-center">
              <span className="material-icons-sharp text-white text-[32px]">bolt</span>
            </div>
            <h3 className="css-67t015 font-[Work_Sans] font-semibold text-[#001d1a] text-[20px] mb-3">
              80% Less Effort
            </h3>
            <p className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] text-[#000000] text-[16px]">
              Dramatically reduce manual effort while maintaining quality and accuracy in your documents
            </p>
          </div>

          {/* Column 3 */}
          <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#0000bd] rounded-full flex items-center justify-center">
              <span className="material-icons-sharp text-white text-[32px]">gps_fixed</span>
            </div>
            <h3 className="css-67t015 font-[Work_Sans] font-semibold text-[#001d1a] text-[20px] mb-3">
              High-Value Focus
            </h3>
            <p className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] text-[#000000] text-[16px]">
              Free your experts to focus on strategic client work instead of document assembly
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const [expandedItem, setExpandedItem] = useState(0); // First item expanded by default

  const accordionItems = [
    {
      title: "Automated Knowledge Graphs",
      content: "Our AI automatically maps your team's collective knowledge, identifying connections between ideas, documents, and expertise that humans might miss. Watch as your scattered information transforms into a living, breathing knowledge ecosystem."
    },
    {
      title: "Always-Learning Intelligence", 
      content: "Every interaction teaches our system something new. As your team works, our AI learns your preferences, writing style, and domain expertise, becoming more valuable with each document you create."
    },
    {
      title: "Purpose-Built for High-stakes Documents",
      content: "Built specifically for mission-critical documents where accuracy and security matter most. Our platform ensures enterprise-grade security while maintaining the flexibility your experts need to create impactful deliverables."
    }
  ];

  return (
    <section className="w-full py-24 px-4 sm:px-6 md:px-9 bg-white relative z-20">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="adjustLetterSpacing css-67t015 font-[Work_Sans] font-semibold leading-[1.2] text-[#001d1a] text-[24px] sm:text-[28px] md:text-[32px] tracking-[-0.225px]">
            Built for Your Workflow, with Your Knowledge.
          </h2>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Animation */}
          <div className="order-2 lg:order-1">
            <div className="p-8 h-96 flex items-center justify-center">
              {expandedItem === 0 ? (
                <KnowledgeGraphAnimation />
              ) : expandedItem === 1 ? (
                <SpiralLearningAnimation />
              ) : (
                <DocumentSecurityAnimation />
              )}
            </div>
          </div>

          {/* Right Column - Accordion */}
          <div className="order-1 lg:order-2">
            <div className="space-y-4">
              {accordionItems.map((item, index) => (
                <div 
                  key={index}
                  className="overflow-hidden"
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => setExpandedItem(expandedItem === index ? -1 : index)}
                    className="w-full px-0 py-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-4">
                      <span className="material-icons-sharp text-[#0000bd] text-[24px]">
                        {index === 0 ? 'account_tree' : index === 1 ? 'psychology' : 'security'}
                      </span>
                      <h3 className="css-67t015 font-[Work_Sans] font-semibold text-[#001d1a] text-[18px]">
                        {item.title}
                      </h3>
                    </div>
                    <span className={`material-icons-sharp text-[#666666] transition-transform duration-500 ease-in-out ${
                      expandedItem === index ? 'rotate-180' : ''
                    }`}>
                      expand_more
                    </span>
                  </button>

                  {/* Accordion Content */}
                  <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    expandedItem === index ? 'max-h-48 sm:max-h-40 md:max-h-32 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-0 pb-4 pt-2">
                      <p className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] text-[#666666] text-[16px]">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CallToActionSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <section className="w-full py-24 px-4 sm:px-6 md:px-9 bg-[#E8F0FF] relative z-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Section Header */}
          <h2 className="adjustLetterSpacing css-67t015 font-[Work_Sans] font-semibold leading-[1.2] text-[#001d1a] text-[24px] sm:text-[28px] md:text-[32px] tracking-[-0.225px] mb-6">
            Free Your Experts to Drive Revenue.
          </h2>
          
          {/* Description */}
          <p className="css-w9luqw font-[Work_Sans] font-normal leading-[24px] text-[#000000] text-[16px] mb-8 max-w-2xl mx-auto">
            Save expert time on every document, increase your proposal velocity, and generate more value for your firm.
          </p>
          
          {/* CTA Button */}
          <motion.div
            className="bg-[#0000bd] h-12 relative rounded-[4px] shrink-0 cursor-pointer shadow-md transition-colors inline-block"
            whileHover={{ 
              scale: 1.05, 
              backgroundColor: "#4040FF"
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
          >
            <div className="flex flex-row items-center justify-center relative size-full">
              <div className="box-border content-stretch flex flex-row gap-2 h-12 items-center justify-center px-6 py-2 relative">
                <div className="css-jjl3q5 flex flex-col font-[Work_Sans] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[18px] text-left text-neutral-50 text-nowrap">
                  <p className="block leading-[28px] whitespace-pre">
                    Request a Demo
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <EarlyAccessForm onClose={() => setIsModalOpen(false)} />
      </Modal>
    </>
  );
}

function BlogButton() {
  return (
    <motion.div
      className="relative z-20"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Link
        to="/blog"
        className="inline-flex items-center justify-center h-12 px-6 bg-transparent border-2 border-[#0000bd] rounded-[4px] hover:bg-[#0000bd] transition-all duration-300 shadow-sm group"
      >
        <span className="text-[#0000bd] font-medium group-hover:text-white transition-colors duration-300">
          Blog
        </span>
      </Link>
    </motion.div>
  );
}

function Header() {
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200/50" data-name="Header">
      <div className="box-border content-stretch flex flex-row items-center justify-between px-4 sm:px-6 md:px-9 py-4 relative w-full">
        <Logo />
        <BlogButton />
      </div>
    </div>
  );
}

export function LandingPage() {
  const [backgroundOpacity, setBackgroundOpacity] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Start fading out at 50% of viewport height, completely hidden by 80%
      const fadeStart = windowHeight * 0.5;
      const fadeEnd = windowHeight * 0.8;
      
      if (scrollPosition <= fadeStart) {
        setBackgroundOpacity(1);
      } else if (scrollPosition >= fadeEnd) {
        setBackgroundOpacity(0);
      } else {
        // Calculate opacity based on scroll progress between fadeStart and fadeEnd
        const fadeProgress = (scrollPosition - fadeStart) / (fadeEnd - fadeStart);
        const opacity = Math.max(0, 1 - fadeProgress);
        setBackgroundOpacity(opacity);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="relative size-full overflow-x-hidden" data-name="Landing">
      {/* Background with animations - smooth fade based on scroll */}
      <div
        style={{ 
          opacity: backgroundOpacity,
          pointerEvents: 'none',
          transition: 'opacity 0.1s ease-out'
        }}
      >
        <Background />
        <GeometricShapes />
        <BackgroundFloatingIcons />
      </div>
      
      {/* Sticky Header */}
      <Header />
      
      {/* Hero Section */}
      <div className="relative size-full min-h-screen flex flex-col pt-20">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 md:px-9">
          <Container />
        </div>
      </div>
      
      {/* Value Proposition Section */}
      <ValuePropositionSection />
      
      {/* Features Section */}
      <FeaturesSection />
      
      {/* Call to Action Section */}
      <CallToActionSection />
    </div>
  );
}
